import { useState } from 'react';
import { User, Car, Phone, LogOut, ChevronRight, Settings, Shield, FileText, Trash2, Edit, Sun, Moon, Monitor, Lock, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EditProfileSheet } from './EditProfileSheet';
import { MyVehiclesSheet } from './MyVehiclesSheet';
import { ChangePasswordSheet } from './ChangePasswordSheet';
import { Button } from './ui/button';
import { useDarkMode, ThemeMode } from '../contexts/DarkModeContext';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { getUserFullName } from '../../types/auth';
import { authService } from '../../services/authService';
import { captureError } from '../../lib/errorHandler';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { SUPPORT_TEL, SUPPORT_WHATSAPP } from '../../lib/support';

interface ProfileViewProps {
  onLogout?: () => void;
}

export function ProfileView({ onLogout }: ProfileViewProps) {
  const { user, logout, updateProfile, setUser } = useAuthStore();
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const navigate = useNavigate();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isVehiclesOpen, setIsVehiclesOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Derive user data directly from auth store
  const userData = {
    name: user ? getUserFullName(user) : 'User',
    email: user?.email || '',
    phone: user?.phone ? `+91 ${user.phone.slice(0, 5)} ${user.phone.slice(5)}` : '',
    isEmailVerified: user?.isEmailVerified || false,
    memberSince: user?.createdAt
      ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : 'Member',
  };

  // Fetch fresh user data before opening edit profile sheet
  const handleEditProfile = async () => {
    if (!user?.id) return;
    setIsLoadingProfile(true);
    try {
      const freshUser = await authService.getUserById(user.id);
      setUser(freshUser);
    } catch (error) {
      captureError(error, 'ProfileView.loadProfile');
    } finally {
      setIsLoadingProfile(false);
      setIsEditProfileOpen(true);
    }
  };

  const handleCallUs = () => {
    window.location.href = SUPPORT_TEL;
  };

  const handleWhatsAppUs = () => {
    window.open(SUPPORT_WHATSAPP, '_blank');
  };

  const handleLogout = () => {
    setIsLogoutDialogOpen(false);
    logout();
    onLogout?.();
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await authService.deleteAccount(user.id);
      setIsDeleteDialogOpen(false);
      logout();
      onLogout?.();
    } catch (error) {
      captureError(error, 'ProfileView.deleteAccount');
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const { theme, isDarkMode, setTheme } = useDarkMode();

  const themeOptions: { mode: ThemeMode; icon: typeof Sun; label: string }[] = [
    { mode: 'light', icon: Sun, label: 'Light' },
    { mode: 'dark', icon: Moon, label: 'Dark' },
    { mode: 'system', icon: Monitor, label: 'System' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 overflow-auto pb-24 transition-colors">
      {/* Header with Avatar */}
      <div className="bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] px-6 pt-8 pb-24 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-32 h-32 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-5 left-5 w-24 h-24 border-2 border-white rounded-full"></div>
        </div>

        {/* Profile Header */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center">
                <User className="w-12 h-12 text-[#8bc43e]" />
              </div>
              <button
                onClick={handleEditProfile}
                disabled={isLoadingProfile}
                className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-[#8bc43e] hover:scale-110 transition-transform disabled:opacity-50"
              >
                <Edit className="w-4 h-4 text-[#8bc43e]" />
              </button>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-1">{userData.name}</h1>
              <p className="text-white/80 text-sm">Member since {userData.memberSince}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Cards */}
      <div className="px-6 -mt-16 relative z-20 space-y-4">
        {/* Contact Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-5 border border-gray-100 dark:border-gray-700">
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <Phone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
                <p className="text-gray-900 dark:text-white font-medium">{userData.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                <p className="text-gray-900 dark:text-white font-medium">{userData.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          {/* Messages moved off the tab bar to make room for the scan button.
              Its unread count rides the Profile tab icon so nothing is hidden. */}
          <button
            onClick={() => navigate('/app/notifications')}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
          >
            <div className="relative w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
              <Bell className="w-6 h-6 text-red-500 dark:text-red-400" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">Messages</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
                  : 'Notifications and updates'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          <button
            onClick={handleEditProfile}
            disabled={isLoadingProfile}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 disabled:opacity-50"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">Edit Profile</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isLoadingProfile ? 'Loading...' : 'Update your personal information'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          {user?.hasPassword && (
            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900 dark:text-white">Change Password</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Update your account password</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>
          )}

          <button
            onClick={() => setIsVehiclesOpen(true)}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
          >
            <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <Car className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">My Vehicles</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage your electric vehicles</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          <button
            onClick={handleWhatsAppUs}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
          >
            <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center">
              <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">WhatsApp Us</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Chat with our support team</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          <button
            onClick={handleCallUs}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">Call Us</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Get help from our support team</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>

        {/* Theme Selector Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                isDarkMode ? 'bg-slate-100 dark:bg-slate-900' : 'bg-amber-50 dark:bg-amber-900/30'
              }`}>
                {isDarkMode ? (
                  <Moon className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                ) : (
                  <Sun className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 dark:text-white">Appearance</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {theme === 'system' ? 'Following system preference' : `${theme === 'dark' ? 'Dark' : 'Light'} mode enabled`}
                </p>
              </div>
            </div>
            {/* Theme Options */}
            <div className="flex gap-2">
              {themeOptions.map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setTheme(mode)}
                  className={`flex-1 flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all ${
                    theme === mode
                      ? 'bg-[#8bc43e] text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Settings & More */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
            className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
              <Settings className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900 dark:text-white">Settings & More</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">App settings and information</p>
            </div>
            <ChevronRight className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${isSettingsExpanded ? 'rotate-90' : ''}`} />
          </button>

          {/* Collapsible Section */}
          {isSettingsExpanded && (
            <div className="border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setIsAboutOpen(true)}
                className="w-full px-5 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center ml-2">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900 dark:text-white">About Us</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Learn more about EVee Buddy</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </button>

              <a
                href="https://www.eveebuddy.com/terms_and_conditions.php"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-5 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
              >
                <div className="w-10 h-10 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center ml-2">
                  <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Terms & Conditions</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Read our terms of service</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </a>

              <a
                href="https://www.eveebuddy.com/privacy_policy.php"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-5 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center ml-2">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Privacy Policy</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">How we handle your data</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </a>

              <button
                onClick={() => setIsDeleteDialogOpen(true)}
                className="w-full px-5 py-3 flex items-center gap-4 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center ml-2">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-red-600 dark:text-red-400">Delete Account</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Permanently delete your account</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </button>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={() => setIsLogoutDialogOpen(true)}
          className="w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 px-5 py-4 flex items-center justify-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <LogOut className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="font-semibold text-red-600 dark:text-red-400">Logout</span>
        </button>

      </div>

      {/* Edit Profile Sheet */}
      <EditProfileSheet
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
        userData={userData}
        onSave={async (data): Promise<string | null> => {
          // Parse full name into firstName and lastName
          const nameParts = data.name.trim().split(' ');
          const firstName = nameParts[0];
          const lastName = nameParts.slice(1).join(' ') || '';

          try {
            await updateProfile({
              firstName,
              lastName,
            });

            // Success - return null (EditProfileSheet will close)
            return null;
          } catch (error) {
            // Return error message (EditProfileSheet will display it and stay open)
            if (error instanceof Error) {
              return error.message;
            }
            return 'Failed to update profile. Please try again.';
          }
        }}
        onEmailUpdated={async () => {
          // Refresh user profile after email update
          if (user?.id) {
            try {
              const freshUser = await authService.getUserById(user.id);
              setUser(freshUser);
            } catch (error) {
              captureError(error, 'ProfileView.refreshAfterEmailUpdate');
            }
          }
        }}
      />

      {/* Change Password Sheet */}
      <ChangePasswordSheet
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
        onSuccess={() => {
          setIsChangePasswordOpen(false);
          // Force logout after password change
          logout();
          onLogout?.();
        }}
      />

      {/* My Vehicles Sheet */}
      <MyVehiclesSheet
        open={isVehiclesOpen}
        onOpenChange={setIsVehiclesOpen}
      />

      {/* Logout Confirmation Dialog */}
      {isLogoutDialogOpen && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-6">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogOut className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">Logout?</h3>
            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              Are you sure you want to logout from your account?
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setIsLogoutDialogOpen(false)}
                className="flex-1 h-12 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLogout}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-6">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">Delete Account?</h3>
            <p className="text-red-600 dark:text-red-400 text-center mb-2 font-semibold text-sm">This action cannot be undone!</p>
            <p className="text-gray-600 dark:text-gray-300 text-center mb-6 text-sm">
              All your data, vehicles, and transaction history will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setIsDeleteDialogOpen(false)}
                className="flex-1 h-12 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* About Us Modal */}
      {isAboutOpen && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
          <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-lg w-full max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">About EVee Buddy</h3>
              <button
                onClick={() => setIsAboutOpen(false)}
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#8bc43e] to-[#6fa02e] flex items-center justify-center shadow-lg">
                <svg width="48" height="48" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12">
                  <circle cx="50" cy="50" r="40" stroke="white" strokeWidth="6"/>
                  <rect x="35" y="32" width="8" height="18" rx="1" fill="white"/>
                  <rect x="57" y="32" width="8" height="18" rx="1" fill="white"/>
                  <rect x="43" y="56" width="14" height="10" rx="1" fill="white"/>
                </svg>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-4 italic text-center">for a worry free travel!</p>
            
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                EVee Buddy is your trusted companion for electric vehicle charging across India. We're committed to making EV charging accessible, convenient, and worry-free.
              </p>
              <p>
                Our mission is to accelerate the adoption of electric vehicles by providing a seamless charging experience with real-time availability, transparent pricing, and reliable service.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mt-4">
                <p className="font-semibold text-gray-900 dark:text-white mb-2">Contact Us</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email: support@pxtenergies.com</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Phone: +91 96451 08642</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Address: Kochi, Kerala, India</p>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-4">
                Version {__APP_VERSION__}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}